import { useQuery } from '@tanstack/react-query';
import { fetchAllPaginated } from '@/lib/supabase-paginate';
import { buildMonthBuckets, computeForecastMonths, toMonthKey } from '@/lib/invoice-forecast-calc';
import type {
  ForecastContribution,
  ForecastInvoiceInput,
  ForecastMonth,
  ForecastProjectInput,
} from '@/lib/invoice-forecast-calc';

type RawProject = ForecastProjectInput & { deleted_at: string | null };

interface RawUser {
  id: string;
  full_name: string | null;
}

export interface InvoiceForecastData {
  months: ForecastMonth[];
  currentMonthKey: string;
  /** VIB-2870: monthKey → prosjektene bak søylen, størst først. */
  contributionsByMonth: Record<string, ForecastContribution[]>;
}

/**
 * Fetches invoices + active projects and hands them to the pure
 * `computeForecastMonths`. All aggregation rules live in
 * `src/lib/invoice-forecast-calc.ts` so they can be unit-tested without
 * Supabase.
 */
export function useInvoiceForecast() {
  return useQuery({
    queryKey: ['invoice-forecast'],
    queryFn: async (): Promise<InvoiceForecastData> => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const curKey = toMonthKey(currentYear, now.getMonth());

      // Buckets span current year + next year so all periods can be computed
      const allMonths = buildMonthBuckets(currentYear, currentYear + 1);

      // Fetch all invoices (paginated — table exceeds 1000 rows in prod)
      const invoices = await fetchAllPaginated<ForecastInvoiceInput>(
        'invoices',
        'invoice_date, amount_excl_vat, project_id',
        (q) => q.not('invoice_date', 'is', null),
      );

      // Fetch all non-deleted projects for BF margin map + remaining estimate
      const projects = await fetchAllPaginated<RawProject>(
        'projects',
        'id, name, project_code, production_lead_id, total_amount, bf_estimated, estimated_deadline, project_status, deleted_at',
        (q) => q.is('deleted_at', null),
      );

      // Production lead names for the drilldown (VIB-2870). Small table; one
      // round trip is cheaper than an embedded join on every project row.
      const users = await fetchAllPaginated<RawUser>('users', 'id, full_name');
      const leadNames = new Map<string, string>();
      for (const u of users) {
        if (u.full_name) leadNames.set(u.id, u.full_name);
      }

      const { months, contributionsByMonth } = computeForecastMonths(
        invoices,
        projects,
        curKey,
        allMonths,
        leadNames,
      );

      return { months, currentMonthKey: curKey, contributionsByMonth };
    },
    staleTime: 5 * 60 * 1000,
  });
}
